import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import { Divider } from '@material-ui/core';

import Menu from './Menu';
import MenuItem from './MenuItem';
import NestedMenuItem from './NestedMenuItem';

import Box from '../../Styled/Box';

const ITEM_HEIGHT = 34;

const mountMenus = (menu, {
  onChange,
  closeMenu,
  nestedProps,
  ...props
}) => (menu && menu.map((item) => (
  <div key={`item-${item.id}`}>
    {item.divider && (
      <Divider />
    )}

    {(!item.options || (item.options && !item.options.length)) && (
      <MenuItem
        onClick={(event) => {
          event.stopPropagation();

          if (onChange) { onChange(item); }
          if (item.onClick) { item.onClick(); }
          if (!item.preventDefault) { closeMenu(); }
        }}
        {...props}
      >
        {item.label}
      </MenuItem>
    )}

    {item.options && item.options.length && (
      <NestedMenuItem
        label={item.label}
        {...nestedProps}
        {...props}
      >
        {mountMenus(item.options, { nestedProps, ...props })}
      </NestedMenuItem>
    )}
  </div>
)));

const NestedMenu = ({
  theme,
  menu,
  menuFooter,
  children,
  menuItemProps,
  itemsOnScreen,
  direction,
  nestedMenuItemProps,
  boxProps,
  onChange,
  stopPropagation,
  otherMenus,
  ...menuProps
}) => {
  const [anchor, setAnchor] = useState(null);

  const openMenu = (event) => {
    if (event && stopPropagation) {
      event.stopPropagation();
    }

    setAnchor(event.currentTarget);
  };

  const closeMenu = (event) => {
    if (event && stopPropagation) {
      event.stopPropagation();
    }

    setAnchor(null);
  };

  const multiMenu = React.useMemo(() => {
    const [first] = (menu || []);
    return Array.isArray(first);
  }, [menu]);

  return (
    <Box display='inline-block' {...boxProps}>
      <div
        role='button'
        tabIndex={0}
        onKeyPress={openMenu}
        onClick={menu ? openMenu : () => {}}
        style={{ display: 'inline-block', outline: 'none' }}
      >
        {children}
      </div>

      <Menu
        // keepMounted
        getContentAnchorEl={null}
        anchorEl={anchor}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={Boolean(anchor)}
        onClose={closeMenu}
        {...menuProps}
        style={{
          transform: 'translateY(10px)',
          ...menuProps.style,
        }}
      >
        {!multiMenu && (
          <div style={{ maxHeight: itemsOnScreen * ITEM_HEIGHT, overflowY: 'auto' }}>
            {mountMenus(menu, {
              closeMenu,
              onChange,
              nestedProps: {
                parentMenuOpen: Boolean(anchor),
                direction,
                ...nestedMenuItemProps,
              },
              ...menuItemProps,
            })}
          </div>
        )}

        {(multiMenu && menu) && menu.map((_menu, i) => (
          // suporte para max height não adicionado aqui
          // eslint-disable-next-line react/no-array-index-key
          <div key={`menu-${i}`}>
            {i > 0 && (
              <Divider />
            )}

            <div
              style={
                i === 0 ? {
                  maxHeight: itemsOnScreen * ITEM_HEIGHT,
                  overflowY: 'auto',
                } : {}
              }
            >
              {mountMenus(_menu, {
                closeMenu,
                onChange,
                nestedProps: {
                  parentMenuOpen: Boolean(anchor),
                  direction,
                  ...nestedMenuItemProps,
                },
                ...menuItemProps,
              })}
            </div>
          </div>
        ))}
      </Menu>
    </Box>
  );
};

NestedMenu.propTypes = {
  theme: PropTypes.object.isRequired,
  menu: PropTypes.array,
  children: PropTypes.any.isRequired,
  menuItemProps: PropTypes.object,
  nestedMenuItemProps: PropTypes.object,
  menuFooter: PropTypes.any,
  itemsOnScreen: PropTypes.number,
  direction: PropTypes.string,
  boxProps: PropTypes.object,
  onChange: PropTypes.func,
  stopPropagation: PropTypes.bool,
  otherMenus: PropTypes.array,
};

NestedMenu.defaultProps = {
  menu: null,
  menuItemProps: {},
  nestedMenuItemProps: {},
  menuFooter: null,
  itemsOnScreen: 5,
  direction: 'ltr',
  boxProps: {},
  onChange: null,
  stopPropagation: true,
  otherMenus: [],
};

export default withTheme(NestedMenu);
